import type { ActorId } from '../actor/actor';
import type { DesignThemeBaseThemeKey } from '../design/designTheme';
import type { EventId } from '../event/event';
import type {
  InvitationDeliveryMethod,
  InvitationDeliveryMode,
  InvitationDeliveryResponseStatus,
  InvitationDeliverySource,
  InvitationDeliveryStatus,
} from '../invitations/delivery';
import type { OccasionId } from '../occasion/occasion';

export type RsvpInvitationId = string;
export type RsvpResponseId = string;

export const RsvpResponseStatus = {
  PENDING: 'pending',
  ATTENDING: 'attending',
  DECLINED: 'declined',
  WAITLISTED: 'waitlisted',
  PROPAGATED_DELETED: 'propagated_deleted',
} as const;
export type RsvpResponseStatus =
  (typeof RsvpResponseStatus)[keyof typeof RsvpResponseStatus];

export const RsvpResponseScope = {
  ACTOR: 'actor',
  HOUSEHOLD: 'household',
  GROUP: 'group',
} as const;
export type RsvpResponseScope =
  (typeof RsvpResponseScope)[keyof typeof RsvpResponseScope];

export const RsvpResponseSource = {
  GUEST: 'guest',
  PLANNER_OVERRIDE: 'planner_override',
} as const;
export type RsvpResponseSource =
  (typeof RsvpResponseSource)[keyof typeof RsvpResponseSource];

export const RsvpInvitationStatus = {
  DRAFT: 'draft',
  SENT: 'sent',
  OPENED: 'opened',
  CLAIMED: 'claimed',
  DELETED: 'deleted',
} as const;
export type RsvpInvitationStatus =
  (typeof RsvpInvitationStatus)[keyof typeof RsvpInvitationStatus];

export const InvitationOpeningExperienceType = {
  NONE: 'none',
  CLASSIC_REVEAL: 'classic-reveal',
  ENVELOPE_OPEN: 'envelope-open',
  SOFT_UNFOLD: 'soft-unfold',
  ELEGANT_LIFT: 'elegant-lift',
  SIMPLE_FADE: 'simple-fade',
  FADE: 'fade',
  ENVELOPE: 'envelope',
  INTERACTIVE_ENVELOPE: 'interactive-envelope',
  FOLDED_CARD: 'folded-card',
  WAX_SEAL: 'wax-seal',
  STORYBOOK: 'storybook',
  ROYAL_GATES: 'royal-gates',
} as const;
export type InvitationOpeningExperienceType =
  (typeof InvitationOpeningExperienceType)[keyof typeof InvitationOpeningExperienceType];

export interface InvitationOpeningExperienceRecord {
  type: InvitationOpeningExperienceType;
  configuration: Record<string, unknown>;
  showOnFirstVisitOnly: boolean;
  allowSkip: boolean;
}

export interface InvitationLanguageDesignRecord {
  publishingAssetId: string | null;
}

export type InvitationLanguageDesigns = Record<
  string,
  InvitationLanguageDesignRecord
>;

export const DEFAULT_INVITATION_OPENING_EXPERIENCE: InvitationOpeningExperienceRecord =
  {
    type: InvitationOpeningExperienceType.CLASSIC_REVEAL,
    configuration: {},
    showOnFirstVisitOnly: true,
    allowSkip: true,
  };

export function normalizeInvitationOpeningExperience(
  value?: Partial<InvitationOpeningExperienceRecord> | null,
): InvitationOpeningExperienceRecord {
  const type =
    value?.type &&
    Object.values(InvitationOpeningExperienceType).includes(value.type)
      ? value.type
      : DEFAULT_INVITATION_OPENING_EXPERIENCE.type;

  return {
    type,
    configuration:
      value?.configuration && !Array.isArray(value.configuration)
        ? { ...value.configuration }
        : {},
    showOnFirstVisitOnly:
      value?.showOnFirstVisitOnly ??
      DEFAULT_INVITATION_OPENING_EXPERIENCE.showOnFirstVisitOnly,
    allowSkip:
      value?.allowSkip ?? DEFAULT_INVITATION_OPENING_EXPERIENCE.allowSkip,
  };
}

export function normalizeInvitationLanguageDesigns(
  value?: Record<string, Partial<InvitationLanguageDesignRecord> | null> | null,
): InvitationLanguageDesigns {
  if (!value || Array.isArray(value)) {
    return {};
  }

  const entries: Array<[string, InvitationLanguageDesignRecord]> = [];

  for (const [localeCode, design] of Object.entries(value)) {
    const normalizedLocaleCode = localeCode.trim().toLowerCase();
    const publishingAssetId =
      typeof design?.publishingAssetId === 'string'
        ? design.publishingAssetId.trim()
        : '';

    if (!normalizedLocaleCode || !publishingAssetId) {
      continue;
    }

    entries.push([
      normalizedLocaleCode,
      {
        publishingAssetId,
      },
    ]);
  }

  return Object.fromEntries(entries);
}

const invitationOpeningExperienceDefaultsByTheme: Record<
  DesignThemeBaseThemeKey,
  InvitationOpeningExperienceType
> = {
  citrus_evening_editorial: InvitationOpeningExperienceType.ENVELOPE_OPEN,
  garden_romance_classic: InvitationOpeningExperienceType.CLASSIC_REVEAL,
  midnight_monogram_modern: InvitationOpeningExperienceType.ELEGANT_LIFT,
  romantic_soft: InvitationOpeningExperienceType.SOFT_UNFOLD,
  classic_formal: InvitationOpeningExperienceType.ENVELOPE_OPEN,
};

const invitationOpeningExperienceDefaultsByTemplateKey: Record<
  string,
  InvitationOpeningExperienceType
> = {
  guest_invitation_classic: InvitationOpeningExperienceType.CLASSIC_REVEAL,
  save_the_date_editorial: InvitationOpeningExperienceType.SOFT_UNFOLD,
  guest_qr_card: InvitationOpeningExperienceType.SIMPLE_FADE,
};

export function resolveDefaultInvitationOpeningExperience(input: {
  baseThemeKey?: DesignThemeBaseThemeKey | null;
  templateKey?: string | null;
}) {
  const suggestedType =
    (input.templateKey
      ? invitationOpeningExperienceDefaultsByTemplateKey[input.templateKey]
      : null) ??
    (input.baseThemeKey
      ? invitationOpeningExperienceDefaultsByTheme[input.baseThemeKey]
      : null) ??
    DEFAULT_INVITATION_OPENING_EXPERIENCE.type;

  return normalizeInvitationOpeningExperience({
    ...DEFAULT_INVITATION_OPENING_EXPERIENCE,
    type: suggestedType,
  });
}

export interface RsvpInvitationRecord {
  id: RsvpInvitationId;
  occasionId: OccasionId;
  invitationKey: string;
  primaryActorId: ActorId | null;
  invitationUnitActorId: ActorId | null;
  responseScope: RsvpResponseScope;
  invitedEventIds: EventId[];
  publishingAssetId: string | null;
  designsByLanguage?: InvitationLanguageDesigns;
  openingExperience: InvitationOpeningExperienceRecord;
  deliveryModeDefault: RsvpInvitationDeliveryMode;
  allowAddOthers: boolean;
  audienceType: RsvpInvitationAudienceType;
  audienceRuleType: RsvpInvitationAudienceRuleType;
  audienceRuleConfig: Record<string, unknown>;
  genericInvitationToken: string | null;
  genericJoinGroupId: ActorId | null;
  language: string | null;
  messageBodyTemplate: string | null;
  status: RsvpInvitationStatus;
  guestDisplayName: string;
  createdAt: string;
  updatedAt: string;
}

export type RsvpInvitationDeliveryMode = 'automatic' | 'manual';

export type RsvpInvitationAudienceType =
  | 'manual_selection'
  | 'all_event_guests'
  | 'group'
  | 'filter'
  | 'anyone_with_link';

export type RsvpInvitationAudienceRuleType =
  'manual_selection' | 'all_event_guests' | 'anyone_with_link' | 'filter';

export interface RsvpInvitationAudienceFilter {
  field: string;
  operator: 'equals' | 'not_equals' | 'contains';
  value: string;
}

export interface RsvpResponseRecord {
  id: RsvpResponseId;
  occasionId: OccasionId;
  invitationId: RsvpInvitationId;
  eventId: EventId;
  responseScope: RsvpResponseScope;
  responseUnitActorId: ActorId | null;
  participantActorIds: ActorId[];
  status: RsvpResponseStatus;
  respondedByActorId: ActorId | null;
  source: RsvpResponseSource;
  note: string | null;
  linkedFormId: string | null;
  linkedFormSubmissionId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RsvpPlannerEventSummary {
  eventId: EventId;
  eventKey: string;
  displayName: string;
  invitedCount: number;
  pendingCount: number;
  attendingCount: number;
  declinedCount: number;
  waitlistedCount: number;
  linkedFormId: string | null;
  deadlineAt: string | null;
  waitlistEnabled: boolean;
}

export interface RsvpPlannerInvitationSummary {
  invitation: RsvpInvitationRecord;
  unitDisplayName: string;
  participantDisplayNames: string[];
  deliveries: RsvpPlannerInvitationDeliverySummary[];
  responses: RsvpResponseRecord[];
}

export interface RsvpPlannerInvitationDeliverySummary {
  id: string;
  invitationId: RsvpInvitationId;
  occasionId: OccasionId;
  eventId: EventId | null;
  communicationDeliveryId: string | null;
  recipientType: string;
  recipientId: string | null;
  actorId: ActorId | null;
  householdId: ActorId | null;
  channel: string;
  status: InvitationDeliveryStatus | string;
  responseStatus: InvitationDeliveryResponseStatus;
  deliveryMode: InvitationDeliveryMode;
  deliveryMethod: InvitationDeliveryMethod | null;
  source: InvitationDeliverySource;
  publicToken: string | null;
  sentAt: string | null;
  manualSentAt: string | null;
  manualSentByUserId: string | null;
  joinedAt: string | null;
  addedByContactId: string | null;
  addedByDeliveryId: string | null;
  sourceDeliveryId: string | null;
  lastActivityAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface RsvpGuestEventState {
  eventId: EventId;
  eventKey: string;
  displayName: string;
  guestSummary: string | null;
  startsAt: string | null;
  locationName: string | null;
  linkedFormId: string | null;
  deadlineAt: string | null;
  waitlistEnabled: boolean;
  response: RsvpResponseRecord | null;
  participantResponses: Array<{
    participantActorId: ActorId;
    participantDisplayName: string;
    response: RsvpResponseRecord | null;
  }>;
}

export interface RsvpInvitationUpsertInput {
  primaryActorId: ActorId | null;
  invitationUnitActorId: ActorId | null;
  responseScope: RsvpResponseScope;
  invitedEventIds: EventId[];
  publishingAssetId?: string | null;
  designsByLanguage?: InvitationLanguageDesigns | null;
  language?: string | null;
  audienceRuleType?: RsvpInvitationAudienceRuleType | null;
  audienceRuleConfig?: Record<string, unknown> | null;
  deliveryModeDefault?: RsvpInvitationDeliveryMode | null;
  allowAddOthers?: boolean | null;
  genericJoinGroupId?: ActorId | null;
  openingExperience?: InvitationOpeningExperienceRecord | null;
  messageBodyTemplate?: string | null;
  guestDisplayName: string | null;
}

export interface RsvpPlannerResponseUpdateInput {
  status: RsvpResponseStatus;
  note: string | null;
}

export interface RsvpGuestEventResponseInput {
  eventId: EventId;
  participantActorId?: ActorId | null;
  status: RsvpResponseStatus;
  note: string | null;
  linkedFormAnswers?: Array<{
    fieldId: string;
    value: string | string[] | boolean | number | null;
  }>;
}
